import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { hoFindDeal } from "../../lib/homeownerRpc";
import { lookupRefusalText } from "../../lib/homeownerLookup";

const LOGO_URL = "/img/bardin-logo.png";

export default function HomeownerLanding() {
  const navigate = useNavigate();
  const [streetNum, setStreetNum] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const trimmedStreet = streetNum.trim();
      const phoneLast4 = phone.replace(/\D/g, "").slice(-4);

      if (phoneLast4.length < 4) {
        setError("Please enter at least 4 digits of your phone number.");
        setLoading(false);
        return;
      }

      // Server-side lookup: street number + last-4 phone -> minimal identity.
      // No direct deals-table read from the public portal anymore.
      const { data: match, error: fetchErr, status } = await hoFindDeal(trimmedStreet, phoneLast4);

      if (fetchErr) {
        // too many failed tries from this device, or lookups paused for everyone: say so and how long
        setError(lookupRefusalText(status, fetchErr) ?? "Something went wrong. Please try again or call us.");
        setLoading(false);
        return;
      }

      if (!match) {
        setError("We couldn't find a matching project. Please check your info and try again.");
        setLoading(false);
        return;
      }

      // Build token and navigate to status page
      const nameParts = (match.homeowner_name || "").trim().split(/\s+/);
      const last = nameParts[nameParts.length - 1];
      const sNum = (match.property_street || "").match(/^(\d+)/)?.[1] || "";
      const token = btoa(`${match.id}:${last}:${sNum}`);

      navigate(`/status?token=${token}`);
    } catch {
      setError("Something went wrong. Please call us at (470) 823-8816.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bardin-sky">
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <img src={LOGO_URL} alt="Bardin Roofing" className="h-12" />
        <a href="tel:+14708238816" className="text-sm text-bardin-teal font-medium">
          (470) 823-8816
        </a>
      </header>

      <main className="max-w-lg mx-auto p-4 pt-8">
        {/* Login card */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="text-center mb-6">
            <img src="/img/duck2.png" alt="Bardin Duck" className="w-16 h-16 mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-bardin-ink">
              Your Project Portal
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Enter your info below to view your project.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-bardin-ink mb-1">Street Number</label>
              <input
                type="text"
                value={streetNum}
                onChange={(e) => setStreetNum(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                placeholder="e.g. 1234"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-bardin-ink mb-1">Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                placeholder="Last 4 digits or full number"
                required
                minLength={4}
              />
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-bardin-orange text-white font-semibold rounded-lg py-3 hover:bg-bardin-orange/90 transition disabled:opacity-50"
            >
              {loading ? "Looking up your project..." : "View My Project"}
            </button>
          </form>
        </div>

        {/* Feature cards */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          {[
            { icon: "📊", label: "Project Status", desc: "Track your progress" },
            { icon: "📄", label: "Documents", desc: "Review paperwork" },
            { icon: "💳", label: "Make a Payment", desc: "Pay online securely" },
            { icon: "📤", label: "Upload Files", desc: "Send us documents" },
          ].map((item) => (
            <div
              key={item.label}
              className="bg-white rounded-xl p-4 shadow-sm text-center"
            >
              <div className="text-2xl mb-1">{item.icon}</div>
              <div className="text-sm font-semibold text-bardin-ink">{item.label}</div>
              <div className="text-xs text-gray-400 mt-0.5">{item.desc}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center text-sm text-gray-400 pb-6">
          <p className="mb-2">
            Need help? Call or text us anytime.
          </p>
          <a
            href="tel:+14708238816"
            className="inline-block bg-bardin-orange text-white font-semibold rounded-lg px-6 py-3 hover:bg-bardin-orange/90 transition"
          >
            Call (470) 823-8816
          </a>
        </div>
      </main>
    </div>
  );
}
